<template>
    <div>
      <h2>Products</h2>
      <div class="sync-buttons">
        <button @click="fetchData('/products')">Refresh Products</button>
        <button @click="syncData('/products/sync')">Sync Products</button>
      </div>
      <div class="items-per-page">
          <span>Items per page:</span>
          <select v-model="itemsPerPage" @change="fetchData('/products', 1)">
              <option value="10">10</option>
              <option value="50">50</option>
              <option value="250">250</option>
          </select>
      </div>
      <div v-if="loading" class="loading-spinner">
        Loading data...
      </div>
      <div v-else>
        <div v-if="totalItems > 0">
            <p>Total Products items: {{ totalItems }}</p>
        </div>
        <table v-if="totalItems > 0">
          <thead>
            <tr>
              <th v-for="(header, index) in headers" :key="index" @click="sortTable(header)">
                {{ formatHeader(header) }}
                <span v-if="sortColumn === header && sortDirection === 'asc'">▲</span>
                <span v-if="sortColumn === header && sortDirection === 'desc'">▼</span>
              </th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(item, index) in products" :key="index">
              <td v-for="(header, index) in headers" :key="index">
                <span v-if="header === 'createdAt'">{{ formatDate(item.node[header]) }}</span>
                <span v-else-if="header === 'id'">{{ item.node[header].replace('gid://shopify/Product/', '') }}</span>
                <span v-else-if="header === 'title'">{{ item.node[header] }}</span>
                <span v-else-if="header === 'productType'">{{ item.node[header] }}</span>
                <span v-else-if="header === 'totalInventory'">{{ item.node[header] }}</span>
                <span v-else-if="header === 'tracked'">{{ item.node[header] ? '✅' : '𐄂' }}</span>
              </td>
            </tr>
          </tbody>
        </table>
        <div v-else>
          <p>No products available.</p>
        </div>
        <div class="pagination" v-if="totalItems > 0">
            <button :disabled="currentPage === 1" @click="fetchData('/products', currentPage - 1)">
                Previous
            </button>
            <span>Page {{ currentPage }} of {{ totalPages }}</span>
            <button :disabled="currentPage === totalPages" @click="fetchData('/products', currentPage + 1)">
                Next
            </button>
        </div>
      </div>
    </div>
  </template>

<script setup>
import { ref, onMounted } from 'vue';
import axios from 'axios';
import { useErrorHandling } from '../composables/errorHandling';
import { useToken } from '../composables/useToken';
import { formatDate } from '../utils/formatters';

const { GetToken } = useToken();
const { handleApiError } = useErrorHandling();

const products = ref([]);
const loading = ref(false);
const headers = ref(['id', 'title', 'productType', 'totalInventory', 'tracked', 'createdAt']);
const sortColumn = ref('');
const sortDirection = ref('asc');
const itemsPerPage = ref(10);
const currentPage = ref(1);
const totalItems = ref(0);
const totalPages = ref(1);

const fetchData = async (url, page = 1) => {
    loading.value = true;

    const data = {
        SessionToken: GetToken(),
        ItemsPerPage: itemsPerPage.value,
        Page: page,
    };
    
    const endpoint = `${process.env.VUE_APP_BASE_URL}${url}`;

    try
    {
        const response = await axios.post(endpoint, data)
        .then(response => {
            return response.data;
        })
        .catch(error => {
            handleApiError(error);
        });

        console.log(`Products Response:`, response);

        products.value.length = 0;
        products.value = response.data;
        currentPage.value = response.Page;
        totalItems.value = response.Total;
        totalPages.value = Math.ceil(totalItems.value / itemsPerPage.value);
    }
    catch (error)
    {
        handleApiError(error.message);
    } finally
    {
        loading.value = false;
    }
};

const syncData = async (url) => {
    loading.value = true;
    try
    {
        const data = {
            SessionToken: GetToken(),
            ItemsPerPage: itemsPerPage.value,
            Page: currentPage.value,
        };
        
        const endpoint = `${process.env.VUE_APP_BASE_URL}${url}`;

        const response = await axios.post(endpoint, data)
        .then(response => {
            return response.data;
        })
        .catch(error => {
            handleApiError(error);
        });

        products.value.length = 0;
        products.value = response.data;
        currentPage.value = response.Page;
        totalItems.value = response.Total;
        totalPages.value = Math.ceil(totalItems.value / itemsPerPage.value);
    }
    catch (error)
    {
        handleApiError(error.message);
    }
    finally
    {
        loading.value = false;
    }
};

const sortTable = (header) => {
  if (sortColumn.value === header) {
    sortDirection.value = sortDirection.value === 'asc' ? 'desc' : 'asc';
  } else {
    sortColumn.value = header;
    sortDirection.value = 'asc';
  }

  products.value.sort((a, b) => {
    const aValue = a[header];
    const bValue = b[header];
    if (aValue < bValue) {
      return sortDirection.value === 'asc' ? -1 : 1;
    }
    if (aValue > bValue) {
      return sortDirection.value === 'asc' ? 1 : -1;
    }
    return 0;
  });
};

const formatHeader = (header) => {
  switch (header)
  {
      case 'id': return 'ID';
      case 'title': return 'Name';
      case 'createdAt': return 'Created At';
      case 'productType': return 'Product Type';
      case 'totalInventory': return 'Inv. QTY';
      case 'tracked': return 'Tracked';

      default: return header;
  }
};

onMounted(() => {
    fetchData('/products');
});
</script>

<style scoped>
.sync-buttons {
  margin-bottom: 10px;
}
.loading-spinner {
  text-align: center;
  margin-top: 20px;
}
table {
    width: 100%;
    border-collapse: collapse;
    margin-top: 20px;
    margin-bottom: 20px;
  }
  th, td {
    border: 1px solid #ddd;
    padding: 8px;
    text-align: left;
  }
  th {
    background-color: #f2f2f2;
    cursor: pointer;
  }
  th:hover {
      background-color: #e0e0e0;
  }

.pagination {
    margin-top: 20px;
    text-align: center;
}

.pagination button {
  margin: 0 5px;
  padding: 5px 10px;
  border: 1px solid #ddd;
  background-color: white;
  cursor: pointer;
}

.pagination button:disabled {
  cursor: not-allowed;
  opacity: 0.6;
}

.items-per-page {
    margin-bottom: 10px;
    display: flex;
    align-items: center;
}

.items-per-page span {
    margin-right: 5px;
}
</style>